import classNames from 'classnames'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { InfoButton } from 'components/buttons'
import { useTracking } from 'lib/analytics'
import { FeatureHighlightSection as FeatureHighlightSectionType } from '../../LandingPage.types'
import css from './FeatureHighlightSection.styles.scss'
import Background from './components/Background'

interface FeatureHighlightSectionProps {
  section: FeatureHighlightSectionType
}

// eslint-disable-next-line complexity
const FeatureHighlightSection = ({
  section: {
    background,
    imagePlacement,
    desktopWidth,
    imageUrl,
    mobileImageUrl,
    imageAltText,
    headerText,
    subHeaderText,
    description,
    callToAction,
    layout,
    isRectangleImage = false,
    backgroundColor,
    mobileOnly = false,
  },
}: FeatureHighlightSectionProps) => {
  const [currentUser] = useCurrentUser()
  const tracker = useTracking()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))
  const isLimitedDesktopWidth = desktopWidth === 'limited'
  const imageLeft = imagePlacement === 'left'
  const isStackedMobile = layout === 'stacked' && isSmallScreen
  const useExtraPadding = background !== 'none' && !isSmallScreen
  const useExtraPaddingLeft =
    useExtraPadding && (imagePlacement === 'right' || !isLargeScreen)
  const useExtraPaddingRight = useExtraPadding && !useExtraPaddingLeft
  const renderCTAOnBottom = layout === 'stacked' && isSmallScreen

  if (!isSmallScreen && mobileOnly) {
    return null
  }

  return (
    <div
      className={classNames(css.FeatureHighlightSection__container, {
        [css['FeatureHighlightSection__container--limitedDesktopWidth']]:
          isLimitedDesktopWidth,
        [css[
          `FeatureHighlightSection__container__background--${backgroundColor}`
        ]]: !!backgroundColor,
      })}
    >
      <div
        className={classNames(css.FeatureHighlightSection__centerContent, {
          [css.FeatureHighlightSection__extraPaddingLeft]: useExtraPaddingLeft,
          [css.FeatureHighlightSection__extraPaddingRight]:
            useExtraPaddingRight,
        })}
      >
        <div
          className={classNames(css.FeatureHighlightSection__contentContainer, {
            [css['FeatureHighlightSection__contentContainer--rectangleImage']]:
              isRectangleImage,
            [css['FeatureHighlightSection__contentContainer--stackedMobile']]:
              isStackedMobile,
            [css[
              'FeatureHighlightSection__contentContainer--limitedDesktopWidth'
            ]]: isLimitedDesktopWidth,
          })}
        >
          <div
            className={classNames(css.FeatureHighlightSection__textContainer, {
              [css['FeatureHighlightSection__textContainer--stackedMobile']]:
                isStackedMobile,
              [css['FeatureHighlightSection__textContainer--imageLeft']]:
                imageLeft,
            })}
          >
            <div className={css.FeatureHighlightSection__headerContainer}>
              <h3 className={css.FeatureHighlightSection__headerText}>
                {headerText}
              </h3>
              {subHeaderText && (
                <h4 className={css.FeatureHighlightSection__subHeaderText}>
                  {subHeaderText}
                </h4>
              )}
            </div>
            <div
              className={classNames(css.FeatureHighlightSection__bodyText, {
                [css['FeatureHighlightSection__bodyText--list']]:
                  description.renderAsListItems,
              })}
            >
              {description.renderAsListItems ? (
                <ul className={css.FeatureHighlightSection__bodyTextList}>
                  {description.content.map((item) => (
                    // eslint-disable-next-line react/no-danger
                    <li dangerouslySetInnerHTML={{ __html: item }} key={item} />
                  ))}
                </ul>
              ) : (
                description.content.map((item) => (
                  <p
                    className={css.FeatureHighlightSection__bodyTextItem}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: item }}
                  />
                ))
              )}
            </div>
            {callToAction && !renderCTAOnBottom && (
              <InfoButton
                pill
                className={classNames(
                  css.FeatureHighlightSection__callToAction,
                  {
                    [css[
                      'FeatureHighlightSection__callToAction--stackedMobile'
                    ]]: isStackedMobile,
                  }
                )}
                href={callToAction.url}
                onClick={() => {
                  if (typeof callToAction.onClick === 'function') {
                    callToAction.onClick(currentUser, tracker)
                  }
                }}
              >
                {callToAction.text}
              </InfoButton>
            )}
          </div>
          <div
            className={classNames(css.FeatureHighlightSection__imageContainer, {
              [css['FeatureHighlightSection__imageContainer--stackedMobile']]:
                isStackedMobile,
              [css['FeatureHighlightSection__imageContainer--rectangleImage']]:
                isRectangleImage,
              [css['FeatureHighlightSection__imageContainer--imageLeft']]:
                imageLeft,
              [css[
                'FeatureHighlightSection__imageContainer--limitedDesktopWidth'
              ]]: isLimitedDesktopWidth,
            })}
          >
            <img
              alt={!!imageAltText ? imageAltText : 'Featured'}
              src={
                isSmallScreen && !!mobileImageUrl ? mobileImageUrl : imageUrl
              }
            />
          </div>
        </div>
      </div>
      {callToAction && renderCTAOnBottom && (
        <InfoButton
          pill
          className={classNames(css.FeatureHighlightSection__callToAction, {
            [css['FeatureHighlightSection__callToAction--stackedMobile']]:
              isStackedMobile,
          })}
          href={callToAction.url}
          onClick={() => {
            if (typeof callToAction.onClick === 'function') {
              callToAction.onClick(currentUser, tracker)
            }
          }}
        >
          {callToAction.text}
        </InfoButton>
      )}
      <Background
        background={background}
        desktopWidth={desktopWidth}
        imagePlacement={imagePlacement}
        layout={layout}
      />
    </div>
  )
}

export default FeatureHighlightSection
